import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  IconArrowLeft,
  IconLoader,
  Popup,
  ajvResolver,
  usePrevious,
  PermissionEnum,
  PermissionDenied,
  ResponseError,
  ButtonPrimary,
  responseErrorsResolver,
  formatBlacklistItem,
  BlacklistData,
  BlacklistUserInfoData,
  IconSelected,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearNewApplicationInfoState,
  setMatchSelected,
  updateStepsStatus,
  clearCofAndMRState,
} from './store/slices';
import {
  useAddNewApplicationCommentMutation,
  useLazyGetNewApplicationsValidatorsQuery,
  useLazyGetNewApplicationCorporateInfoQuery,
  useCreateCorporateApplicationMutation,
  useUpdateCorporateApplicationMutation,
  useLazyGetCorporateApplicationSystemLogsQuery,
  useUpdateCorporateApplicationStatusMutation,
  useLazyGetCorporateNewApplicationDocumentsQuery,
  useLazyGetApplicationAgreementsQuery,
  useLazyGetApplicationChatQuery,
  useLazyGetApplicationOngoingMonitoringQuery,
  useApplyApplicationOngoingWarningActionMutation,
  useBatchApplyApplicationOngoingWarningActionMutation,
  useLazyGetApplicationOngoingStatusQuery,
  useRestartOngoingCheckingMutation,
  useLazyGetNewApplicationDefaultDocumentCategoriesQuery,
  useLazyGetApplicationQuestionnarieQuery,
  useUpdateCorporateQuestionnarieMutation,
  useResetApplicationWarningActionMutation,
  useLazyGetNewApplicationCorporateOrganizationQuery,
  useLazyGetApplicationWarningGroupWithPaginationQuery,
  useLazyGetApplicationOngoingWarningQuery,
  useLazyGetApplicationRiskScoreQuery,
  useCreateApplicationRiskScoreMutation,
  useLazyGetApplicationAmlUpdatingItemQuery,
  useLazyGetApplicationLastCalculatingScoreQuery,
  useLazyGetRelationShipToCustomerDictionaryQuery,
  useLazyGetSourceOfWealthForCorporateDictionaryQuery,
  useLazyGetBusinessActivitiesDictionaryQuery,
  useLazyGetCustomersMainClientsDictionaryQuery,
  useLazyGetSalesChannelsDictionaryQuery,
  useLazyGetCustomerSourceOfWealthQuery,
  useLazyGetCustomerSourceOfFundsQuery,
  useLazyGetAnnualIncomeDictionaryQuery,
  useLazyGetTypeOfAccountsDictionaryQuery,
  useLazyGetAccountsCurrenciesQuery,
  useCreateAccountsCurrencyMutation,
  useUpdateAccountsCurrencyMutation,
  useDeleteAccountsCurrencyMutation,
  useLazyGetAccountsFlowsQuery,
  useCreateAccountsFlowMutation,
  useUpdateAccountsFlowMutation,
  useDeleteAccountsFlowMutation,
  useLazyGetExpectedFundsOriginsQuery,
  useCreateExpectedFundsOriginsMutation,
  useUpdateExpectedFundsOriginsMutation,
  useDeleteExptectedFundsOriginsMutation,
} from '../store/services';
import StepItem from '../../../components/new-application/step-item';
import NewApplicationInfoTop from '../new-application-info-top';
import NewApplicationCorporateInfoStepOne from '../../../components/new-application/corporate/step-one';
import NewApplicationCorporateInfoStepTwo from '../../../components/new-application/corporate/step-two';
import NewApplicationCorporateInfoStepThree from '../../../components/new-application/corporate/step-three';
import NewApplicationCorporateInfoStepFour from '../../../components/new-application/corporate/step-four';
import NewApplicationCorporateInfoStepFive from '../../../components/new-application/corporate/step-five';
import NewApplicationCorporateInfoStepSix from '../../../components/new-application/corporate/step-six';
import NewApplicationCorporateInfoStepSeven from '../../../components/new-application/corporate/step-seven';
import NewApplicationCorporateInfoStepEight from '../../../components/new-application/ongoing-monitoring';
import {
  NewApplicationStepStatus,
  CorporateFormValues,
  ApplicationType,
  NewApplicationStatus,
  ChannelStatus,
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  ExpectedFundsOriginsFormValues,
  AccountsCurrencies,
} from '../types';
import { updateApplicationStepStatuses } from './helpers/update-application-step-statuses';
import getCorporateAppicationBody from './helpers/get-corporate-application-body';
import {
  CorporateQuestionnarieFormValues,
  OngoingCheckingStatus,
  OngoingMonitring,
  OngoingStatus,
  CustomerScore,
  CustomerScoreUpdateStatus,
} from '../../customers/types';
import {
  useGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersEmployeesListQuery,
  useLazyGetCustomersApplicationSchemesQuery,
} from '../../../store/dictionaries/services';
import getCorporateQuestionnarieBody from './helpers/get-questionnarie-body';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

// eslint-disable-next-line no-useless-escape
const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;

const schema = {
  type: 'object',
  properties: {},
  required: [],
};

let retryTimeout: NodeJS.Timeout;
let timeInterval: NodeJS.Timeout;

const NewApplicationCorporateInfo = () => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [defaultQuestionnarieFormValues, setDefaultQuestionnarieFormValues] =
    useState({});
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [initialFields, setInitialFields] = useState({});
  const [formattedSchema, setFormattedSchema] = useState(schema);
  const [statusChangeError, setStatusChangeError] = useState('');
  const [applyError, setApplyError] = useState('');
  const [link, setLink] = useState('');
  const [customersSuppliersStepEnabled, setCustomersSuppliersStepEnbaled] =
    useState(false);
  const [applicationLoadErrorVisible, setApplicationLoadErrorVisible] =
    useState(false);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [userCreated, setUserCreated] = useState(false);
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [blacklistDataVisible, setBlacklistDataVisible] =
    useState<boolean>(false);
  const [blacklistItem, setBlacklistDataItem] =
    useState<BlacklistUserInfoData>();
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [totalIncomeError, setTotalIncomeError] = useState('');
  const [accountsCurrenciesValues, setAccountsCurrenciesValues] = useState<
    AccountsCurrenciesFormValues[]
  >([
    {
      id: 'accounts-currency-1',
      accountOpeningPurpose: '',
      currencyId: '',
      plannedTransactionNature: '',
    },
  ]);
  const [selectedAccountsCurrencyId, setSelectedAccountsCurrencyId] =
    useState('');
  const [accountsFlowsValues, setAccountsFlowsValues] = useState<
    AccountsFlowsFormValues[]
  >([]);
  const [expectedFundsOriginsValues, setExpectedFundsOriginsValues] = useState<
    ExpectedFundsOriginsFormValues[]
  >([
    {
      id: 'expected-funds-origins-1',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'INCOMING',
    },
    {
      id: 'expected-funds-origins-2',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'OUTGOING',
    },
  ]);
  const [selectedExpectedFundsOriginsId, setSelectedFundsOriginsId] =
    useState('');

  const linkRef = useRef<HTMLAnchorElement | null>(null);

  const {
    newApplications: {
      corporateInfo: {
        newApplicationInfo,
        stepsStatus: applicationStepsStatus,
        systemLogs,
        documents,
        agreements,
        mainRepresentativeApplications,
        companyOfficialsApplications,
        ongoingMonitoring,
        ongoingCheckingStatus,
        questionnarie,
        changeLogs,
        changeLogsPagination,
        riskScore,
        amlUpdateStatus,
        relationshipToTheCustomerDictionary,
        sourceOfWealth,
        busniessActivities,
        customerMainCleints,
        salesChannels,
        customerSourceOfWealth,
        customerSourceOfFunds,
        annualIncomes,
        typeOfAccounts,
        accountsCurrencies,
        questionnarieId,
        accountsFlows,
        expectedFundsOrigins,
      },
      list: { validators, applicationChat, allowedDocumentsCategories },
    },
    customersDictionaries: {
      countries,
      activePermissions,
      infoDictionariesIsLoading,
      employeesList,
      currentEmployee,
      applicationSchemes,
      currencies,
    },
  } = useAppSelector((state) => state);
  const {
    baseCurrency,
    settings,
    currencies: currenciesList,
  } = useAppSelector((state) => state.root);

  const companyLegalName = settings?.find(
    (item) => item.key === 'COMPANY-LEGAL-NAME',
  )?.data?.value;

  const {
    control,
    reset,
    formState: { errors, isDirty },
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<CorporateFormValues>({
    resolver: ajvResolver(formattedSchema),
  });

  const {
    control: questionnarieControl,
    reset: questionnarieReset,
    clearErrors: questionnarieClearErrors,
    formState: { errors: questionnarieErrors, isDirty: questionnarieIsDirty },
    watch: questionnarieWatch,
    getValues: questionnarieGetValues,
    setError: questionnarieSetError,
    setValue: questionnarieSetValue,
  } = useForm<CorporateQuestionnarieFormValues>();

  const navigate = useNavigate();

  const fields = watch();
  const questionnarieFields = questionnarieWatch();

  const websiteLink = watch('websiteLink');
  const prevWebsiteLink = usePrevious(websiteLink);

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const stepsStatus = updateApplicationStepStatuses({
    values: fields,
    stepsStatus: applicationStepsStatus,
    documents,
    agreements,
    mainRepresentativeApplications,
    companyOfficialsApplications,
    ongoingMonitoring,
    ongoingCheckingStatus,
    documentCategories: allowedDocumentsCategories,
    applicationScheme: newApplicationInfo?.applicationScheme,
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
  });

  const prevStepsStatus = usePrevious(stepsStatus);
  const prevApplicationId = usePrevious(applicationId);

  useGetCustomersInfoDictionariesQuery();

  const [getApplicationChat] = useLazyGetApplicationChatQuery();
  const [getInfo, { isLoading, isFetching }] =
    useLazyGetNewApplicationCorporateInfoQuery();
  const [getApplicationOrganization] =
    useLazyGetNewApplicationCorporateOrganizationQuery();
  const [getValidators] = useLazyGetNewApplicationsValidatorsQuery();
  const [create] = useCreateCorporateApplicationMutation();
  const [update, { isLoading: requestInProgress }] =
    useUpdateCorporateApplicationMutation();
  const [createNewCorporateCustomer, { isLoading: createInProgress }] =
    useUpdateCorporateApplicationStatusMutation();
  const [
    rejectCorporateApplication,
    {
      isLoading: rejectInProgress,
      isSuccess: rejectIsSuccess,
      isError: rejectIsError,
    },
  ] = useUpdateCorporateApplicationStatusMutation();
  const [backToIncomplete, { isLoading: backToIncompleteInProgress }] =
    useUpdateCorporateApplicationStatusMutation();
  const [
    addResponsibleToApplication,
    {
      isLoading: addResponsibleInProgress,
      isSuccess: addResponsibleIsSuccess,
      isError: addResponsibleIsError,
    },
  ] = useUpdateCorporateApplicationStatusMutation();
  const [getSystemLogs] = useLazyGetCorporateApplicationSystemLogsQuery();
  const [
    addComment,
    { isLoading: sendCommentInProgress, isSuccess: sendCommentIsSuccess },
  ] = useAddNewApplicationCommentMutation();
  const [getDocuments] = useLazyGetCorporateNewApplicationDocumentsQuery();
  const [
    getApplicationAgreements,
    { isLoading: agreementsLoading, isFetching: agreementsFetching },
  ] = useLazyGetApplicationAgreementsQuery();
  const [getCustomersEmployeesList] = useLazyGetCustomersEmployeesListQuery();
  const [
    getApplicationOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetApplicationOngoingMonitoringQuery();
  const [
    applyApplicationOngoingWarning,
    { isLoading: applyWarningInProgress },
  ] = useApplyApplicationOngoingWarningActionMutation();
  const [
    batchApplyApplicationOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyApplicationOngoingWarningActionMutation();
  const [getOngoingMonitoringStatus, { isLoading: ongoingStatusIsLoading }] =
    useLazyGetApplicationOngoingStatusQuery();
  const [restartOngoingChecking, { isLoading: restartInProgress }] =
    useRestartOngoingCheckingMutation();
  const [getDefaultDocumentCategories] =
    useLazyGetNewApplicationDefaultDocumentCategoriesQuery();
  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();

  const [
    getApplicationQuestionnarie,
    { isLoading: questionnarieIsLoading, isFetching: questionnarieIsFetching },
  ] = useLazyGetApplicationQuestionnarieQuery();
  const [
    updateCorporateQuestionnarie,
    { isLoading: questionnarieUpdateInProgress },
  ] = useUpdateCorporateQuestionnarieMutation();
  const [
    resetApplicationWarningsWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetApplicationWarningActionMutation();
  const [
    getApplicationWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetApplicationWarningGroupWithPaginationQuery();
  const [
    getApplicationOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetApplicationOngoingWarningQuery();
  const [getApplicationRiskScore, { isLoading: riskScoreIsLoading }] =
    useLazyGetApplicationRiskScoreQuery();
  const [getApplicationAmlUpdatingItem] =
    useLazyGetApplicationAmlUpdatingItemQuery();
  const [getApplicationLastCalculaingScore] =
    useLazyGetApplicationLastCalculatingScoreQuery();
  const [
    createApplicationScore,
    { isSuccess, isLoading: riskScoreRequestInProgress },
  ] = useCreateApplicationRiskScoreMutation();
  const [getRelationshipDictionary] =
    useLazyGetRelationShipToCustomerDictionaryQuery();
  const [getSourceOfWealthForCorporateDictionary] =
    useLazyGetSourceOfWealthForCorporateDictionaryQuery();
  const [getBusinessActivitiesDictionary] =
    useLazyGetBusinessActivitiesDictionaryQuery();
  const [getCustomersMainClientsDictionary] =
    useLazyGetCustomersMainClientsDictionaryQuery();
  const [getSalesChannels] = useLazyGetSalesChannelsDictionaryQuery();
  const [getCustomerSourceOfWealth] = useLazyGetCustomerSourceOfWealthQuery();
  const [getCustomerSourceOfFunds] = useLazyGetCustomerSourceOfFundsQuery();
  const [getAnnualIncomeDictionary] = useLazyGetAnnualIncomeDictionaryQuery();
  const [getTypeOfAccountsDictionary] =
    useLazyGetTypeOfAccountsDictionaryQuery();

  const [
    getAccountsCurrencies,
    {
      isLoading: accountsCurrenciesIsLoading,
      isFetching: accountsCurrenciesIsFetching,
    },
  ] = useLazyGetAccountsCurrenciesQuery();
  const [
    createAccountsCurrencies,
    { isLoading: createAccountsCurrenciesIsProgress },
  ] = useCreateAccountsCurrencyMutation();
  const [
    updateAccountsCurrencies,
    { isLoading: updateAccountsCurrenciesInProgress },
  ] = useUpdateAccountsCurrencyMutation();
  const [
    deleteAccountsCurrencies,
    {
      isLoading: deleteAccountsCurrenciesInProgress,
      isSuccess: deleteAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteAccountsCurrencyMutation();
  const [
    getAccountsFlows,
    { isLoading: accountsFlowsIsLoading, isFetching: accountsFlowsIsFetching },
  ] = useLazyGetAccountsFlowsQuery();
  const [createAccountsFlows, { isLoading: createAccountsFlowsIsProgress }] =
    useCreateAccountsFlowMutation();
  const [updateAccountsFlows, { isLoading: updateAccountsFlowsInProgress }] =
    useUpdateAccountsFlowMutation();
  const [deleteAccountsFlows] = useDeleteAccountsFlowMutation();
  const [
    getExpectedFundsOrigins,
    {
      isLoading: expectedFundsOriginsIsLoading,
      isFetching: expectedFundsOriginsIsFetching,
    },
  ] = useLazyGetExpectedFundsOriginsQuery();
  const [
    createExpectedFundsOrigins,
    { isLoading: createExpectedFundsOriginsInProgress },
  ] = useCreateExpectedFundsOriginsMutation();
  const [
    updateExpectedFundsOrigins,
    { isLoading: updateExpectedFundsOriginsInProgress },
  ] = useUpdateExpectedFundsOriginsMutation();
  const [
    deleteExpectedFundsOrigins,
    {
      isLoading: deleteExpectedFundsOriginsInProgress,
      isSuccess: deleteExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteExptectedFundsOriginsMutation();

  const onUpdate = useCallback(() => {
    createApplicationScore(applicationId as string);
  }, [applicationId]);

  const onSaveExpectedFundsOrigins = async () => {
    const expectedFundsOriginsForCreation: ExpectedFundsOriginsFormValues[] =
      [];
    const expectedFundsOriginsForUpdate: ExpectedFundsOriginsFormValues[] = [];

    expectedFundsOriginsValues?.forEach((item) => {
      if (item.id?.includes('expected-funds-origins')) {
        expectedFundsOriginsForCreation.push(item);
      } else {
        const currentExpectedFundsOrigins = expectedFundsOrigins?.find(
          (value) => value.id === item.id,
        );

        if (currentExpectedFundsOrigins) {
          if (
            !isEqual(item, {
              id: currentExpectedFundsOrigins.id,
              currencyId: currentExpectedFundsOrigins.currencyId,
              mainCounterpartiesAndWebsites:
                currentExpectedFundsOrigins.mainCounterpartiesAndWebsites,
              countryOfIncorporationId:
                currentExpectedFundsOrigins.countryOfIncorporationId,
              counterpartyBankCountryIds:
                currentExpectedFundsOrigins.counterpartyBankCountryIds,
              activityCountryIds:
                currentExpectedFundsOrigins.activityCountryIds,
              mainActivities: currentExpectedFundsOrigins.mainActivities,
              transactionReason: currentExpectedFundsOrigins.transactionReason,
              transactionType: currentExpectedFundsOrigins.transactionType,
            })
          ) {
            expectedFundsOriginsForUpdate.push(item);
          }
        }
      }
    });

    if (expectedFundsOriginsForCreation?.length) {
      await Promise.all(
        expectedFundsOriginsForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createExpectedFundsOrigins({
              id: questionnarieId as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });

            return response;
          }),
      );
    }

    if (expectedFundsOriginsForUpdate?.length) {
      await Promise.all(
        expectedFundsOriginsForUpdate?.map(async (item) => {
          const response = await updateExpectedFundsOrigins({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item.countryOfIncorporationId || '',
              counterpartyBankCountryIds: item.counterpartyBankCountryIds || '',
              activityCountryIds: item.activityCountryIds || '',
              mainActivities: item.mainActivities || '',
              transactionReason: item.transactionReason || '',
              transactionType: item.transactionType || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsCurrencies = async () => {
    const accountsCurrenciesForCreation: AccountsCurrenciesFormValues[] = [];
    const accountsCurrenciesForUpdate: AccountsCurrenciesFormValues[] = [];

    accountsCurrenciesValues?.forEach((item) => {
      if (item.id?.includes('accounts-currency')) {
        accountsCurrenciesForCreation.push(item);
      } else {
        const currentAccountsCurrency = accountsCurrencies?.find(
          (currency) => currency.id === item.id,
        );

        if (currentAccountsCurrency) {
          if (
            !isEqual(item, {
              id: currentAccountsCurrency.id,
              currencyId: currentAccountsCurrency.currencyId,
              accountOpeningPurpose:
                currentAccountsCurrency.accountOpeningPurpose,
              plannedTransactionNature:
                currentAccountsCurrency.plannedTransactionNature,
            })
          ) {
            accountsCurrenciesForUpdate.push(item);
          }
        }
      }
    });

    if (accountsCurrenciesForCreation?.length) {
      await Promise.all(
        accountsCurrenciesForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createAccountsCurrencies({
              id: questionnarieId as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });

            return response;
          }),
      );
    }

    if (accountsCurrenciesForUpdate?.length) {
      await Promise.all(
        accountsCurrenciesForUpdate?.map(async (item) => {
          const response = await updateAccountsCurrencies({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose || '',
              plannedTransactionNature: item.plannedTransactionNature || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsFlows = async () => {
    const accountsFlowsForCreation: AccountsFlowsFormValues[] = [];
    const accountsFlowsForUpdate: AccountsFlowsFormValues[] = [];

    accountsFlowsValues?.forEach((item) => {
      if (item.id?.includes('accounts-flow')) {
        accountsFlowsForCreation.push(item);
      } else {
        const currentAccountsFlow = accountsFlows?.find(
          (flow) => flow.id === item.id,
        );

        if (currentAccountsFlow) {
          if (
            !isEqual(item, {
              id: currentAccountsFlow.id,
              transactionType: currentAccountsFlow.transactionType,
              currencyId: currentAccountsFlow.currencyId,
              expectedAnnualCreditTurnover:
                currentAccountsFlow.expectedAnnualCreditTurnover.toString(),
              maxTransferAmountPerTransaction:
                currentAccountsFlow.maxTransferAmountPerTransaction.toString(),
              transactionFrequency:
                currentAccountsFlow.transactionFrequency.toString(),
              transactionFrequencyPeriodMonths:
                currentAccountsFlow.transactionFrequencyPeriodMonths.toString(),
            })
          ) {
            accountsFlowsForUpdate.push(item);
          }
        }
      }
    });

    if (
      accountsFlowsForCreation?.filter(
        (item) =>
          item.expectedAnnualCreditTurnover &&
          item.maxTransferAmountPerTransaction &&
          item.transactionFrequency &&
          item.transactionFrequencyPeriodMonths,
      )?.length
    ) {
      await Promise.all(
        accountsFlowsForCreation
          ?.filter(
            (item) =>
              item.expectedAnnualCreditTurnover &&
              item.maxTransferAmountPerTransaction &&
              item.transactionFrequency &&
              item.transactionFrequencyPeriodMonths,
          )
          ?.map(async (item) => {
            const response = await createAccountsFlows({
              id: questionnarieId as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });

            return response;
          }),
      );
    }

    if (accountsFlowsForUpdate?.length) {
      await Promise.all(
        accountsFlowsForUpdate?.map(async (item) => {
          const response = await updateAccountsFlows({
            id: item.id as string,
            body: {
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: Number(
                item.expectedAnnualCreditTurnover,
              ),
              maxTransferAmountPerTransaction: Number(
                item.maxTransferAmountPerTransaction,
              ),
              transactionFrequency: Number(item.transactionFrequency),
              transactionFrequencyPeriodMonths: Number(
                item.transactionFrequencyPeriodMonths,
              ),
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteAccountCurrencies = async (id: string) => {
    if (id.includes('accounts-currency')) {
      setAccountsCurrenciesValues(
        accountsCurrenciesValues?.filter((item) => item.id !== id),
      );

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          setAccountsFlowsValues(
            accountsFlowsValues?.filter((item) =>
              currentAccountsFlows.every((flow) => item.id !== flow.id),
            ),
          );
        }

        if (currentExpectedFundsOrigins?.length) {
          setExpectedFundsOriginsValues(
            expectedFundsOriginsValues?.filter((item) =>
              currentExpectedFundsOrigins.every(
                (expectedFund) => item.id !== expectedFund.id,
              ),
            ),
          );
        }
      }
    } else {
      setSelectedAccountsCurrencyId(id);

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          await Promise.all(
            currentAccountsFlows
              ?.filter((item) => !item.id?.includes('accounts-flow'))
              ?.map(async (item) => {
                const response = await deleteAccountsFlows(item.id as string);

                return response;
              }),
          );

          if (
            currentAccountsFlows?.filter((item) =>
              item.id?.includes('accounts-flow'),
            ).length
          ) {
            const notCreatedAccountsFlow = currentAccountsFlows?.filter(
              (item) => item.id?.includes('accounts-flow'),
            );

            setAccountsFlowsValues(
              accountsFlowsValues?.filter((item) =>
                notCreatedAccountsFlow.every((flow) => item.id !== flow.id),
              ),
            );
          }
        }

        if (currentExpectedFundsOrigins?.length) {
          await Promise.all(
            currentExpectedFundsOrigins
              ?.filter((item) => !item.id?.includes('expected-funds-origins'))
              ?.map(async (item) => {
                const response = await deleteExpectedFundsOrigins(
                  item.id as string,
                );

                return response;
              }),
          );

          if (
            currentExpectedFundsOrigins?.filter((item) =>
              item.id?.includes('expected-funds-origins'),
            )?.length
          ) {
            const notCreatedExpectedFundsOrigins =
              currentExpectedFundsOrigins?.filter((item) =>
                item.id?.includes('expected-funds-origins'),
              );

            setExpectedFundsOriginsValues(
              expectedFundsOriginsValues?.filter((item) =>
                notCreatedExpectedFundsOrigins.every(
                  (expectedFundsOrigin) => item.id !== expectedFundsOrigin.id,
                ),
              ),
            );
          }
        }
      }

      await deleteAccountsCurrencies(id);
    }
  };

  const onDeleteExpectedFundsOrigins = async (id: string) => {
    if (id.includes('expected-funds-origins')) {
      setExpectedFundsOriginsValues(
        expectedFundsOriginsValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedFundsOriginsId(id);

      await deleteExpectedFundsOrigins(id);
    }
  };

  const getApplicationRiskScoreAction = useCallback(async () => {
    (await getApplicationRiskScore(applicationId as string)) as {
      data: { data: CustomerScore };
    };
  }, [applicationId]);

  const getApplicationLasCalculaingScoreAction = useCallback(
    async (amlUpdateId: string) => {
      const { data } = await getApplicationAmlUpdatingItem({
        orgId: applicationId as string,
        id: amlUpdateId,
      });

      if (
        data?.data.status === CustomerScoreUpdateStatus.FAIL ||
        data?.data.status === CustomerScoreUpdateStatus.SUCCESS
      ) {
        clearInterval(timeInterval);
      }

      if (data?.data.status === CustomerScoreUpdateStatus.SUCCESS) {
        getApplicationRiskScore(applicationId as string);
      }
    },
    [],
  );

  const getApplicationOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getApplicationOngoingWarningById({
      appId: applicationId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const onApplicationCOFSubmit = ({
    id,
    body,
  }: {
    id: string;
    body: CorporateFormValues;
  }) => {
    update({
      body,
      id,
    });
  };

  const getApplicationWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getApplicationWarning({
      id: applicationId as string,
      warningGroupId,
      cursor,
    });
  };

  const batchApplyApplicationOngoingWarningAction = async ({
    action,
    comment,
    matchStatus,
  }: {
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const ongoingGroupsWithoutWanings: OngoingMonitring[] = [];

    ongoingMonitoring?.forEach((item) => {
      item?.warnings?.edges?.forEach((warning) => {
        if (!warning.node.action) {
          ongoingGroupsWithoutWanings.push(item);
        }
      });
    });

    if (ongoingGroupsWithoutWanings && ongoingGroupsWithoutWanings.length) {
      await Promise.all(
        ongoingGroupsWithoutWanings?.map(async (item) => {
          const response = (await batchApplyApplicationOngoingWarning({
            appId: applicationId as string,
            body: {
              warningGroupId: item.id,
              action,
              employeeId: currentEmployee?.id as string,
              comment,
              matchStatus,
            },
          })) as { error: ResponseError };

          if (response.error) {
            if (response.error.data.message) {
              if (
                Array.isArray(response.error.data.message) &&
                response.error.data.message[0].property === 'coreError'
              ) {
                setApplyError(response.error.data.message[0].value as string);
              } else if (typeof response.error.data.message === 'string') {
                setApplyError(response.error.data.message);
              } else {
                setApplyError('Internal Server Error. Try Again');
              }
            }
          }

          return response;
        }),
      );
    }
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetApplicationWarningsWarning({
      appId: applicationId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const applyApplicationOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyApplicationOngoingWarning({
      appId: applicationId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const addCommentAction = useCallback(
    (text: string) => {
      addComment({
        id: applicationId as string,
        body: {
          text,
        },
      });
    },
    [applicationId],
  );

  const onRejectAplication = async () => {
    setStatusChangeError('');

    const response = (await rejectCorporateApplication({
      id: applicationId as string,
      body: { status: NewApplicationStatus.DECLINED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onBackToIncomplete = async () => {
    setStatusChangeError('');

    const response = (await backToIncomplete({
      id: applicationId as string,
      body: { status: NewApplicationStatus.INCOMPLETE },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onAddResponsible = async (data: {
    status: NewApplicationStatus;
    id?: string | undefined;
  }) => {
    setStatusChangeError('');

    const response = addResponsibleToApplication({
      id: applicationId as string,
      body: {
        status: data.status,
        reviewEmployeeId: data.id,
      },
    }) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const getSystemLogsAction = useCallback(() => {
    if (applicationId) {
      getSystemLogs(applicationId);
    }
  }, [applicationId]);

  const onSubmit = handleSubmit(async (values) => {
    setApplyError('');

    const body = getCorporateAppicationBody(values);

    if (!body.websiteLink) {
      body.websiteLink = '';
    }

    if (applicationId) {
      const response = (await update({
        body,
        id: applicationId,
      })) as { error?: ResponseError };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setApplyError,
        });
      }

      if (
        newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.accountantDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.auditedByExternalComplianceActive ||
          newApplicationInfo?.applicationScheme
            ?.changesInManagementOrOwnershipActive ||
          newApplicationInfo?.applicationScheme
            ?.complianceConsultantDetailsActive ||
          newApplicationInfo?.applicationScheme?.fcaNumberActive ||
          newApplicationInfo?.applicationScheme?.hmrcMlrNumberActive ||
          newApplicationInfo?.applicationScheme
            ?.hmrcRegistrationExpiryDateActive ||
          newApplicationInfo?.applicationScheme?.icoNumberActive ||
          newApplicationInfo?.applicationScheme?.itProviderDetailsActive ||
          newApplicationInfo?.applicationScheme?.mlroDetailsActive ||
          newApplicationInfo?.applicationScheme?.numberOfAgentsActive ||
          newApplicationInfo?.applicationScheme?.numberOfBranchesActive ||
          newApplicationInfo?.applicationScheme
            ?.onboardCorporateClientsActive ||
          newApplicationInfo?.applicationScheme?.onboardMsbActive ||
          newApplicationInfo?.applicationScheme?.paymentsForCharitiesActive ||
          newApplicationInfo?.applicationScheme?.payoutPartnerDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.regulatoryInvestigationActive ||
          newApplicationInfo?.applicationScheme?.screeningActive ||
          newApplicationInfo?.applicationScheme?.transactionMonitoringActive ||
          newApplicationInfo?.applicationScheme?.typeOfBusinessLicenceActive ||
          newApplicationInfo?.applicationScheme?.visitedByAuthoritiesActive ||
          newApplicationInfo?.applicationScheme
            ?.workingWithAnyOtherIPSPActive ||
          newApplicationInfo?.applicationScheme?.id ===
            'cdbb8c71-90e6-4a5e-964f-e55169e0c4df')
      ) {
        const questionnarieValues = questionnarieGetValues();

        const questionnarieBody = getCorporateQuestionnarieBody({
          questionnarieValues,
          scheme: newApplicationInfo?.applicationScheme,
        });

        const questionnarieResponse = (await updateCorporateQuestionnarie({
          id: newApplicationInfo?.id,
          body: questionnarieBody,
        })) as { error?: ResponseError };

        if (questionnarieResponse?.error) {
          responseErrorsResolver({
            errors: questionnarieResponse.error,
            setError: questionnarieSetError,
            setGlobalError: setApplyError,
          });
        }

        if (
          newApplicationInfo?.applicationScheme?.id ===
          'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
        ) {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }
      }
    } else {
      create(body);
    }
  });

  const getInfoAction = useCallback(async () => {
    if (applicationId) {
      const response = (await getInfo(applicationId)) as {
        error: ResponseError;
        data: any;
      };

      getApplicationChat(applicationId);
      getDefaultDocumentCategories(applicationId);

      if (activePermissions?.includes(PermissionEnum.USERS_LIST)) {
        getCustomersEmployeesList();
      }

      if (
        activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
        )
      ) {
        getDocuments(applicationId);
      }

      if (response) {
        if (response?.error?.data?.message) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0].property === 'coreError'
          ) {
            setApplicationLoadErrorVisible(true);
          }
        }
      }
    }
  }, [applicationId, activePermissions]);

  const onCreateCustomer = async () => {
    setStatusChangeError('');

    const response = (await createNewCorporateCustomer({
      id: applicationId as string,
      body: { status: NewApplicationStatus.ACCEPTED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    } else {
      setUserCreated(true);

      setTimeout(() => {
        getApplicationOrganization(applicationId as string);
      }, 8000);
    }
  };

  const retryOngoinStatusAction = async () => {
    clearTimeout(retryTimeout);

    const response = (await getOngoingMonitoringStatus(
      newApplicationInfo?.id as string,
    )) as { data?: { data?: { edges: { node: OngoingCheckingStatus }[] } } };

    if (response?.data?.data) {
      if (
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.SUCCESS,
        ) ||
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.FAIL,
        )
      ) {
        clearTimeout(retryTimeout);
      } else {
        retryTimeout = setTimeout(() => {
          retryOngoinStatusAction();
        }, 60000);
      }
    }
  };

  const restartOngoingCheckingAction = async () => {
    const response = (await restartOngoingChecking({
      applicationId: newApplicationInfo?.id as string,
    })) as { data?: { data?: OngoingCheckingStatus } };

    if (response?.data) {
      retryOngoinStatusAction();
    }
  };

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    let nextSchema: any = schema;

    if (websiteLink) {
      nextSchema = {
        ...schema,
        properties: {
          ...schema.properties,
          websiteLink: {
            type: 'string',
            regexp: urlRegex.toString(),
            errorMessage: 'Must be a URL address',
          },
        },
      };
    } else if (!websiteLink && prevWebsiteLink) {
      nextSchema = schema;
    }

    setFormattedSchema(nextSchema);
  }, [websiteLink, prevWebsiteLink]);

  useEffect(() => {
    if (errors && Object.keys(errors).length) {
      setLink(`#${Object.keys(errors)[0]}`);
    }
  }, [errors]);

  useEffect(() => {
    if (link && linkRef && linkRef.current) {
      (linkRef.current as { click: VoidFunction }).click();

      setLink('');
    }
  }, [link, linkRef]);

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      dispatch(clearCofAndMRState());

      setTimeout(() => {
        getInfoAction();
        getSystemLogsAction();
      }, 10);
    }
  }, [applicationId, getInfoAction, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    if (!validators) {
      getValidators();
    }
  }, [getValidators, validators]);

  useEffect(() => {
    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }
  }, [accountsCurrencies]);

  useEffect(() => {
    if (accountsFlows && accountsCurrencies) {
      const nextAccountsFlows = accountsFlows?.map((item) => ({
        id: item.id,
        transactionType: item.transactionType,
        currencyId: item.currencyId,
        expectedAnnualCreditTurnover:
          item.expectedAnnualCreditTurnover.toString(),
        maxTransferAmountPerTransaction:
          item.maxTransferAmountPerTransaction.toString(),
        transactionFrequency: item.transactionFrequency.toString(),
        transactionFrequencyPeriodMonths:
          item.transactionFrequencyPeriodMonths.toString(),
      }));

      const notCreatedCurrencies: AccountsCurrencies[] = [];

      accountsCurrencies?.forEach((item) => {
        if (
          !nextAccountsFlows?.find(
            (flow) => flow.currencyId === item.currencyId,
          )
        ) {
          notCreatedCurrencies.push(item);
        }
      });

      if (notCreatedCurrencies?.length) {
        notCreatedCurrencies?.forEach((item) => {
          nextAccountsFlows.push(
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'INCOMING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'OUTGOING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
          );
        });
      }

      setAccountsFlowsValues(nextAccountsFlows);
    }
  }, [accountsFlows, accountsCurrencies]);

  useEffect(() => {
    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  }, [expectedFundsOrigins]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      newApplicationInfo.status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      ) &&
      !ongoingCheckingStatus
    ) {
      retryOngoinStatusAction();
    }
  }, [newApplicationInfo, activePermissions, ongoingCheckingStatus]);

  useEffect(() => {
    if (
      ongoingCheckingStatus &&
      ongoingCheckingStatus?.status === OngoingStatus.SUCCESS &&
      newApplicationInfo &&
      newApplicationInfo.status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      ) &&
      !ongoingMonitoring
    ) {
      getApplicationOngoingMonitoring(newApplicationInfo.id);
    }
  }, [ongoingCheckingStatus, newApplicationInfo, ongoingMonitoring]);

  useEffect(() => {
    if (prevStepsStatus && !isEqual(stepsStatus, prevStepsStatus)) {
      dispatch(updateStepsStatus(stepsStatus));
    }
  }, [stepsStatus, prevStepsStatus, dispatch]);

  useEffect(() => {
    if (rejectIsSuccess || rejectIsError) {
      setPopupVisible(false);
    }
  }, [rejectIsSuccess, rejectIsError]);

  useEffect(() => {
    if (
      !applicationSchemes &&
      activePermissions?.includes(PermissionEnum.APPLICATIONS_SCHEMES_LIST)
    ) {
      getCustomersApplicationSchemes();
    }
  }, [activePermissions, applicationSchemes]);

  useEffect(() => {
    if (deleteAccountsCurrenciesIsSuccess) {
      setSelectedAccountsCurrencyId('');
    }
  }, [deleteAccountsCurrenciesIsSuccess]);

  useEffect(() => {
    if (deleteExpectedFundsOriginsIsSuccess) {
      setSelectedFundsOriginsId('');
    }
  }, [deleteExpectedFundsOriginsIsSuccess]);

  useEffect(() => {
    if (newApplicationInfo) {
      if (newApplicationInfo?.applicationScheme) {
        setCustomersSuppliersStepEnbaled(
          Boolean(
            newApplicationInfo?.applicationScheme?.customersEnabled ||
            newApplicationInfo?.applicationScheme?.suppliersEnabled ||
            (newApplicationInfo?.applicationScheme?.customersEnabled === null &&
              newApplicationInfo?.applicationScheme?.suppliersEnabled === null),
          ),
        );

        if (
          !questionnarie &&
          (newApplicationInfo?.applicationScheme?.accountantDetailsActive ||
            newApplicationInfo?.applicationScheme
              ?.auditedByExternalComplianceActive ||
            newApplicationInfo?.applicationScheme
              ?.changesInManagementOrOwnershipActive ||
            newApplicationInfo?.applicationScheme
              ?.complianceConsultantDetailsActive ||
            newApplicationInfo?.applicationScheme?.fcaNumberActive ||
            newApplicationInfo?.applicationScheme
              ?.hmrcMlrNumberAndExpiryDateActive ||
            newApplicationInfo?.applicationScheme?.icoNumberActive ||
            newApplicationInfo?.applicationScheme?.itProviderDetailsActive ||
            newApplicationInfo?.applicationScheme?.mlroDetailsActive ||
            newApplicationInfo?.applicationScheme?.numberOfAgentsActive ||
            newApplicationInfo?.applicationScheme?.numberOfBranchesActive ||
            newApplicationInfo?.applicationScheme
              ?.onboardCorporateClientsActive ||
            newApplicationInfo?.applicationScheme?.onboardMsbActive ||
            newApplicationInfo?.applicationScheme?.paymentsForCharitiesActive ||
            newApplicationInfo?.applicationScheme?.payoutPartnerDetailsActive ||
            newApplicationInfo?.applicationScheme
              ?.regulatoryInvestigationActive ||
            newApplicationInfo?.applicationScheme?.screeningActive ||
            newApplicationInfo?.applicationScheme
              ?.transactionMonitoringActive ||
            newApplicationInfo?.applicationScheme
              ?.typeOfBusinessLicenceActive ||
            newApplicationInfo?.applicationScheme?.visitedByAuthoritiesActive ||
            newApplicationInfo?.applicationScheme
              ?.workingWithAnyOtherIPSPActive ||
            newApplicationInfo?.hasQuestionnaire)
        ) {
          getApplicationQuestionnarie(newApplicationInfo?.id);
        }
      } else {
        setCustomersSuppliersStepEnbaled(true);
      }
    }
  }, [newApplicationInfo, questionnarie]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getApplicationRiskScoreAction();
    }
  }, [newApplicationInfo, activePermissions]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getApplicationLastCalculaingScore(applicationId as string);
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      const amlUpdateId = localStorage.getItem('applicationAmlUpdateId');

      if (amlUpdateId) {
        timeInterval = setInterval(() => {
          getApplicationLasCalculaingScoreAction(amlUpdateId);
        }, 15000);
      }
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!relationshipToTheCustomerDictionary) {
        getRelationshipDictionary();
      }

      if (!sourceOfWealth) {
        getSourceOfWealthForCorporateDictionary();
      }

      if (!busniessActivities) {
        getBusinessActivitiesDictionary();
      }

      if (!customerMainCleints) {
        getCustomersMainClientsDictionary();
      }

      if (!salesChannels) {
        getSalesChannels();
      }

      if (!customerSourceOfWealth) {
        getCustomerSourceOfWealth();
      }

      if (!customerSourceOfFunds) {
        getCustomerSourceOfFunds();
      }

      if (!annualIncomes) {
        getAnnualIncomeDictionary();
      }

      if (!typeOfAccounts) {
        getTypeOfAccountsDictionary();
      }
    }
  }, [
    activePermissions,
    newApplicationInfo,
    relationshipToTheCustomerDictionary,
    sourceOfWealth,
    busniessActivities,
    customerMainCleints,
    salesChannels,
    customerSourceOfWealth,
    customerSourceOfFunds,
    annualIncomes,
    typeOfAccounts,
  ]);

  useEffect(() => {
    if (questionnarie) {
      questionnarieReset(questionnarie);
      setDefaultQuestionnarieFormValues(questionnarie);
    }
  }, [questionnarie]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      questionnarieId &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
      )
    ) {
      if (!accountsCurrencies) {
        getAccountsCurrencies(questionnarieId);
      }

      if (!accountsFlows) {
        getAccountsFlows(questionnarieId);
      }

      if (!expectedFundsOrigins) {
        getExpectedFundsOrigins(questionnarieId);
      }
    }
  }, [questionnarieId, newApplicationInfo, accountsCurrencies, accountsFlows]);

  useEffect(() => {
    if (isSuccess && riskScore) {
      setSuccessMessageVisible(true);

      setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 6000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (newApplicationInfo) {
      let amlCtfComplianceProgram: string | null = null;
      let requiresBusinessLicense: string | null = null;

      if (newApplicationInfo?.amlCtfComplianceProgram !== null) {
        if (newApplicationInfo?.amlCtfComplianceProgram) {
          amlCtfComplianceProgram = 'yes';
        } else {
          amlCtfComplianceProgram = 'no';
        }
      }

      if (newApplicationInfo?.requiresBusinessLicense !== null) {
        if (newApplicationInfo?.requiresBusinessLicense) {
          requiresBusinessLicense = 'yes';
        } else {
          requiresBusinessLicense = 'no';
        }
      }

      const formValues = {
        countryOfIncorporationId:
          newApplicationInfo.countryOfIncorporationId || '',
        companyName: newApplicationInfo.companyName || '',
        registeredCountryId: newApplicationInfo.registeredCountryId || '',
        registeredCity: newApplicationInfo.registeredCity || '',
        registeredPostalCode: newApplicationInfo.registeredPostalCode || '',
        registeredAddress1: newApplicationInfo.registeredAddress1 || '',
        registeredAddress2: newApplicationInfo.registeredAddress2 || '',
        businessCountryId: newApplicationInfo.businessCountryId || '',
        businessCity: newApplicationInfo.businessCity || '',
        businessPostalCode: newApplicationInfo.businessPostalCode || '',
        businessAddress1: newApplicationInfo.businessAddress1 || '',
        businessAddress2: newApplicationInfo.businessAddress2 || '',
        correspondenceCountryId:
          newApplicationInfo.correspondenceCountryId || '',
        correspondenceCity: newApplicationInfo.correspondenceCity || '',
        correspondencePostalCode:
          newApplicationInfo.correspondencePostalCode || '',
        correspondenceAddress1: newApplicationInfo.correspondenceAddress1 || '',
        correspondenceAddress2: newApplicationInfo.correspondenceAddress2 || '',
        dateOfIncorporation: newApplicationInfo.dateOfIncorporation || '',
        companyNumber: newApplicationInfo.companyNumber || '',
        sicCodes: newApplicationInfo?.sicCodes || [],
        businessLicense: newApplicationInfo.businessLicense || '',
        websiteLink: newApplicationInfo.websiteLink || '',
        countriesOfInterestIds: newApplicationInfo.countriesOfInterestIds || [],
        businessDescription: newApplicationInfo.businessDescription || '',
        businessAddressSameAsRegisteredAddress:
          newApplicationInfo.businessAddressSameAsRegisteredAddress || false,
        correspondenceAddressSameAsRegisteredAddress:
          newApplicationInfo.correspondenceAddressSameAsRegisteredAddress ||
          false,
        monthlyTurnoverRangeId: newApplicationInfo.monthlyTurnoverRangeId || '',
        monthlyNumberIncomingTransactionsRangeId:
          newApplicationInfo.monthlyNumberIncomingTransactionsRangeId || '',
        monthlyNumberOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyNumberOutgoingTransactionsRangeId || '',
        monthlyValueIncomingTransactionsRangeId:
          newApplicationInfo.monthlyValueIncomingTransactionsRangeId || '',
        monthlyValueOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyValueOutgoingTransactionsRangeId || '',
        monthlyMaxValueIncomingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueIncomingPaymentRangeId || '',
        monthlyMaxValueOutgoingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueOutgoingPaymentRangeId || '',
        productIds:
          newApplicationInfo?.products?.edges.map((item) => item.node.id) || [],
        suppliers:
          newApplicationInfo?.suppliers?.map((item) => ({
            id: item.id,
            name: item.name,
            monthlyTurnoverRangeId: item.monthlyTurnoverRangeId,
          })) || [],
        customers:
          newApplicationInfo?.customers?.map((item) => ({
            id: item.id,
            name: item.name,
            monthlyTurnoverRangeId: item.monthlyTurnoverRangeId,
          })) || [],

        deliveryChannelId: newApplicationInfo?.deliveryChannelId || '',
        reviewDuplicatesComment:
          newApplicationInfo.reviewDuplicatesComment || '',
        reviewedDuplicates: newApplicationInfo.reviewedDuplicates || [],
        vatNumer: newApplicationInfo?.vatNumber || '',
        amlCtfComplianceProgram,
        requiresBusinessLicense,
        requiredCurrenciesCryptoId:
          newApplicationInfo?.requiredCurrenciesCryptoId || [],
        purposeOfAccount: newApplicationInfo?.purposeOfAccount || '',
        expectedTypeOfClients: newApplicationInfo?.expectedTypeOfClients || [],
      };

      setDefaultFormValues({
        ...formValues,
        companyOfficial:
          companyOfficialsApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
        mainRepresentative:
          mainRepresentativeApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
      });
      reset({
        ...formValues,
        companyOfficial:
          companyOfficialsApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
        mainRepresentative:
          mainRepresentativeApplications?.map((item) => ({
            id: item.id,
            position: item.position,
            status: item.status,
            percentage: item.percentage,
            natureOfControl: item.natureOfControl,
            subApplication: item.subApplication,
          })) || [],
      });
      setInitialFields(formValues);

      if (
        (!newApplicationInfo?.applicationScheme ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme.agreementsActive) ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme.agreementsActive === null)) &&
        !agreements
      ) {
        getApplicationAgreements(newApplicationInfo.id);
      }
    }
  }, [reset, dispatch, newApplicationInfo]);

  useEffect(() => {
    if (
      (addResponsibleIsSuccess || addResponsibleIsError) &&
      applicationId &&
      !documents
    ) {
      getDocuments(applicationId);
    }
  }, [
    addResponsibleIsSuccess,
    addResponsibleIsError,
    applicationId,
    documents,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();

      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    if (
      !isEqual(fields, defaultFormValues) ||
      !isEqual(questionnarieFields, defaultQuestionnarieFormValues)
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    fields,
    defaultFormValues,
    questionnarieFields,
    defaultQuestionnarieFormValues,
  ]);

  useEffect(() => {
    let title = 'New Application';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  useEffect(
    () => () => {
      clearInterval(timeInterval);
    },
    [],
  );

  let content;

  if (
    !userCreated &&
    (isLoading ||
      isFetching ||
      infoDictionariesIsLoading ||
      agreementsLoading ||
      agreementsFetching ||
      questionnarieIsLoading ||
      (!questionnarie && questionnarieIsFetching))
  ) {
    content = (
      <div className={cn.newApplicationInfoContentLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    (!isLoading &&
      !isFetching &&
      !infoDictionariesIsLoading &&
      !agreementsLoading &&
      !agreementsFetching &&
      !questionnarieIsLoading &&
      ((newApplicationInfo?.hasQuestionnaire && questionnarie) ||
        !newApplicationInfo?.hasQuestionnaire) &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_LIST,
      )) ||
    userCreated
  ) {
    if (applicationLoadErrorVisible) {
      content = (
        <div className={cn.applicationLoadError}>
          <div className={cn.applicationLoadErrorTitle}>
            Something went wrong. Reload page
          </div>
          <ButtonPrimary
            text="Reload"
            className={cn.reloadButton}
            onClick={() => window.location.reload()}
            bold
          />
        </div>
      );
    } else {
      content = (
        <div className={cn.newApplicationInfoContent}>
          <NewApplicationInfoTop
            status={newApplicationInfo?.status}
            reviewStatus={newApplicationInfo?.reviewStatus}
            setPopupVisible={(data) => setPopupVisible(data)}
            stepsStatus={applicationStepsStatus}
            setMatchSelected={(data) => dispatch(setMatchSelected(data))}
            onSendComment={(text) => addCommentAction(text)}
            matches={newApplicationInfo?.similarApplications?.edges}
            applicationType={ApplicationType.CORPORATE}
            reviewedDuplicates={newApplicationInfo?.reviewedDuplicates}
            applicationId={applicationId}
            comments={newApplicationInfo?.comments?.edges.map(
              (item) => item?.node,
            )}
            createdDate={newApplicationInfo?.createdAt as string}
            reviewEmployee={newApplicationInfo?.reviewEmployee}
            sendCommentInProgress={sendCommentInProgress}
            sendCommentIsSuccess={sendCommentIsSuccess}
            onSubmit={onSubmit}
            onCreate={() => onCreateCustomer()}
            onAddResponsible={(data) => onAddResponsible(data)}
            onBackToIncomplete={() => onBackToIncomplete()}
            backToIncompleteInProgress={backToIncompleteInProgress}
            createAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_CREATE_ACTION,
            )}
            createInProgress={createInProgress}
            requestInProgress={
              requestInProgress ||
              questionnarieUpdateInProgress ||
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress
            }
            reviewDuplicatesComment={
              newApplicationInfo?.reviewDuplicatesComment
            }
            fields={fields}
            questionnarieFields={questionnarieFields}
            defaultFormValues={defaultFormValues}
            defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
            isEditable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            )}
            canSelectResponsible={activePermissions?.includes(
              PermissionEnum.USERS_LIST,
            )}
            addResponsibleInProgress={addResponsibleInProgress}
            addResponsibleIsSuccess={addResponsibleIsSuccess}
            statusChangeError={statusChangeError}
            employeesList={employeesList}
            organizationId={newApplicationInfo?.organizationId}
            title={
              newApplicationInfo
                ? newApplicationInfo.companyName
                : 'New Application'
            }
            applicationChat={applicationChat?.find(
              (item) => item.status === ChannelStatus.OPEN,
            )}
            riskScore={riskScore}
            riskScoreIsLoading={riskScoreIsLoading}
            applicationScheme={newApplicationInfo?.applicationScheme}
            setError={setError}
            clearErrors={clearErrors}
            customersSuppliersStepEnabled={customersSuppliersStepEnabled}
            isCorporate
            questionnarieSetError={questionnarieSetError}
            questionnarieClearErrors={questionnarieClearErrors}
            changeLogs={changeLogs}
            changeLogsPagination={changeLogsPagination}
            systemLogs={systemLogs}
            client={newApplicationInfo?.user}
            riskScoreUpdateIsSuccess={isSuccess}
            riskScoreRequestInProgress={riskScoreRequestInProgress}
            amlUpdateStatus={amlUpdateStatus}
            onUpdate={onUpdate}
            getApplicationRiskScore={getApplicationRiskScoreAction}
            totalIncomeError={totalIncomeError}
            onSaveAccountsCurrencies={onSaveAccountsCurrencies}
            accountsCurrenciesValues={accountsCurrenciesValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose,
                plannedTransactionNature: item.plannedTransactionNature,
              }))}
            accountsCurrencies={accountsCurrencies?.map((item) => ({
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose,
              plannedTransactionNature: item.plannedTransactionNature,
            }))}
            onSaveAccountsFlows={onSaveAccountsFlows}
            accountsFlowsValues={accountsFlowsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover:
                  item.expectedAnnualCreditTurnover?.toString() || '',
                maxTransferAmountPerTransaction:
                  item.maxTransferAmountPerTransaction?.toString() || '',
                transactionFrequency:
                  item.transactionFrequency?.toString() || '',
                transactionFrequencyPeriodMonths:
                  item.transactionFrequencyPeriodMonths?.toString() || '',
              }))}
            accountsFlows={accountsFlows?.map((item) => ({
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover:
                item.expectedAnnualCreditTurnover?.toString() || '',
              maxTransferAmountPerTransaction:
                item.maxTransferAmountPerTransaction?.toString() || '',
              transactionFrequency: item.transactionFrequency?.toString() || '',
              transactionFrequencyPeriodMonths:
                item.transactionFrequencyPeriodMonths?.toString() || '',
            }))}
            onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            expectedFundsOriginsValues={expectedFundsOriginsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item?.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item?.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item?.counterpartyBankCountryIds || [],
                activityCountryIds: item?.activityCountryIds || [],
                mainActivities: item?.mainActivities || '',
                transactionReason: item?.transactionReason || '',
                transactionType: item?.transactionType || '',
              }))}
            expectedFundsOrigins={expectedFundsOrigins?.map((item) => ({
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item?.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item?.countryOfIncorporationId || '',
              counterpartyBankCountryIds:
                item?.counterpartyBankCountryIds || [],
              activityCountryIds: item?.activityCountryIds || [],
              mainActivities: item?.mainActivities || '',
              transactionReason: item?.transactionReason || '',
              transactionType: item?.transactionType || '',
            }))}
            stepOneBlockLoadingInProgress={
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress
            }
          />
          <StepItem
            title="Step 1"
            status={applicationStepsStatus?.stepOne as NewApplicationStepStatus}
            id="step-1"
            zIndex={10}
          >
            <NewApplicationCorporateInfoStepOne
              control={control}
              errors={errors}
              countries={countries}
              fields={fields}
              sicCodes={newApplicationInfo?.sicCodes}
              countriesOfInterest={newApplicationInfo?.countriesOfInterestIds}
              setValue={setValue}
              status={newApplicationInfo?.status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              applicationScheme={newApplicationInfo?.applicationScheme}
              clearErrors={clearErrors}
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              questionnarieFields={questionnarieFields}
              requiredCurrencies={
                newApplicationInfo?.requiredCurrenciesCryptoId
              }
              currencies={currencies}
              regulatedCompanyScheme={Boolean(
                newApplicationInfo?.applicationScheme?.id ===
                'cdbb8c71-90e6-4a5e-964f-e55169e0c4df',
              )}
              relationshipToTheCustomerDictionary={
                relationshipToTheCustomerDictionary
              }
              sourceOfWealth={sourceOfWealth}
              baseCurrency={baseCurrency}
              busniessActivities={busniessActivities}
              customerMainCleints={customerMainCleints}
              salesChannels={salesChannels}
              questionnarieSetValue={questionnarieSetValue}
              customerSourceOfWealth={customerSourceOfWealth}
              customerSourceOfFunds={customerSourceOfFunds}
              companyLegalName={companyLegalName as string}
              annualIncomes={annualIncomes}
              totalIncomeError={totalIncomeError}
              setTotalIncomeError={setTotalIncomeError}
              typeOfAccounts={typeOfAccounts}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              accountsFlowsValues={accountsFlowsValues}
              setAccountsFlowsValues={setAccountsFlowsValues}
              currenciesList={currenciesList}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
            />
          </StepItem>
          {customersSuppliersStepEnabled && (
            <StepItem
              title="Step 2"
              status={
                applicationStepsStatus?.stepTwo as NewApplicationStepStatus
              }
              id="step-2"
              zIndex={9}
            >
              <NewApplicationCorporateInfoStepTwo
                suppliers={newApplicationInfo?.suppliers}
                customers={newApplicationInfo?.customers}
                setValue={setValue}
                status={newApplicationInfo?.status}
                isEditable={activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                )}
                errors={errors}
                clearErrors={clearErrors}
                applicationScheme={newApplicationInfo?.applicationScheme}
              />
            </StepItem>
          )}
          <StepItem
            title={customersSuppliersStepEnabled ? 'Step 3' : 'Step 2'}
            status={
              applicationStepsStatus?.stepThree as NewApplicationStepStatus
            }
            id="step-3"
            zIndex={8}
          >
            <NewApplicationCorporateInfoStepThree
              control={control}
              status={newApplicationInfo?.status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              errors={errors}
              clearErrors={clearErrors}
            />
          </StepItem>
          <StepItem
            title={customersSuppliersStepEnabled ? 'Step 4' : 'Step 3'}
            status={
              applicationStepsStatus?.stepFour as NewApplicationStepStatus
            }
            id="step-4"
            zIndex={7}
          >
            <NewApplicationCorporateInfoStepFour
              mainRepresentativeApplications={mainRepresentativeApplications}
              applicationId={applicationId}
              setValue={setValue}
              status={newApplicationInfo?.status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              errors={errors}
              applicationStatus={newApplicationInfo?.status}
              initialFields={initialFields}
              fields={fields}
              onSubmit={onApplicationCOFSubmit}
              setDefaultFormValues={setDefaultFormValues}
              defaultFormValues={defaultFormValues}
              companyOfficial={companyOfficialsApplications}
              questionnarieFields={questionnarieFields}
              defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
              applicationScheme={newApplicationInfo?.applicationScheme}
              getApplicationChat={getApplicationChat}
              onSaveAccountsCurrencies={onSaveAccountsCurrencies}
              onSaveAccountsFlows={onSaveAccountsFlows}
              onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            />
          </StepItem>
          <StepItem
            title={customersSuppliersStepEnabled ? 'Step 5' : 'Step 4'}
            status={
              applicationStepsStatus?.stepFive as NewApplicationStepStatus
            }
            id="step-5"
            zIndex={6}
          >
            <NewApplicationCorporateInfoStepFive
              setValue={setValue}
              mainRepresentativeApplications={mainRepresentativeApplications}
              companyOfficial={companyOfficialsApplications}
              applicationId={applicationId}
              status={newApplicationInfo?.status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              applicationStatus={newApplicationInfo?.status}
              initialFields={initialFields}
              setDefaultFormValues={setDefaultFormValues}
              defaultFormValues={defaultFormValues}
              fields={fields}
              onSubmit={onApplicationCOFSubmit}
              onSaveAccountsCurrencies={onSaveAccountsCurrencies}
              onSaveAccountsFlows={onSaveAccountsFlows}
              onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            />
          </StepItem>
          <StepItem
            title={customersSuppliersStepEnabled ? 'Step 6' : 'Step 5'}
            status={
              applicationStepsStatus?.stepEight as NewApplicationStepStatus
            }
            id="step-6"
            zIndex={5}
          >
            <NewApplicationCorporateInfoStepEight
              status={newApplicationInfo?.status}
              currentEmployee={currentEmployee}
              activePermissions={activePermissions}
              isLoading={
                ongoingIsLoading || ongoingIsFetching || ongoingStatusIsLoading
              }
              ongoingMonitoring={ongoingMonitoring}
              isAllowed={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
              )}
              applyError={applyError}
              applyWarningInProgress={applyWarningInProgress}
              applyOngoingWarning={applyApplicationOngoingWarningAction}
              batchApplyWarningInProgress={batchApplyWarningInProgress}
              batchApplyOngoingWarning={
                batchApplyApplicationOngoingWarningAction
              }
              ongoingCheckingStatus={ongoingCheckingStatus}
              restartOngoingCheckingAction={restartOngoingCheckingAction}
              restartInProgress={restartInProgress}
              batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
              onResetTransactionWarningAction={onResetTransactionWarningAction}
              resetTransactionsWarningIsLoading={
                resetTransactionsWarningIsLoading
              }
              resetTransactionsWarningIsSuccess={
                resetTransactionsWarningIsSuccess
              }
              selectedWarningId={selectedWarningId}
              getApplicationWarningsAction={getApplicationWarningsAction}
              paginationInProgress={
                paginationInProgress || paginationIsFetching
              }
              selectedWarningGroup={selectedWarningGroup}
              blacklistData={blacklistData}
              setBlacklistData={setBlacklistData}
              blacklistDataVisible={blacklistDataVisible}
              setBlacklistDataVisible={setBlacklistDataVisible}
              blacklistItem={blacklistItem}
              setBlacklistDataItem={setBlacklistDataItem}
              blacklistItemVisible={blacklistItemVisible}
              setBlacklistItemVisible={setBlacklistItemVisible}
              getOngoingWarningById={getApplicationOngoingWarningByIdAction}
              isWarningByIdLoading={isWarningByIdLoading}
              isWarningByIdFetching={isWarningByIdFetching}
            />
          </StepItem>
          <StepItem
            title={customersSuppliersStepEnabled ? 'Step 7' : 'Step 6'}
            status={applicationStepsStatus?.stepSix as NewApplicationStepStatus}
            id="step-7"
            zIndex={4}
          >
            <NewApplicationCorporateInfoStepSix
              documents={documents}
              applicationId={applicationId}
              applicationStatus={newApplicationInfo?.status}
              search={search}
              allowedDocumentsCategories={allowedDocumentsCategories}
              errors={errors}
              clearErrors={clearErrors}
              renameAvailable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
              )}
            />
          </StepItem>
          {(!newApplicationInfo?.applicationScheme ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme.agreementsActive) ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme.agreementsActive ===
                null)) && (
            <StepItem
              title={customersSuppliersStepEnabled ? 'Step 8' : 'Step 7'}
              status={
                applicationStepsStatus?.stepSeven as NewApplicationStepStatus
              }
              id="step-8"
              zIndex={3}
            >
              <NewApplicationCorporateInfoStepSeven
                agreements={agreements}
                errors={errors}
              />
            </StepItem>
          )}
        </div>
      );
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!userCreated) {
      content = <PermissionDenied />;
    }
  }

  return (
    <div className={cn.newApplicationInfo}>
      <div className={cn.newApplicationLinkWrapper}>
        <button
          type="button"
          className={cn.newApplicationBackLink}
          onClick={() => {
            if (isDirty || questionnarieIsDirty) {
              setDirtyPopupVisible(true);
            } else {
              navigate(
                `${ROUTES.CUSTOMERS_NEW_APPLICATION_LIST}${search || ''}`,
              );
              dispatch(clearNewApplicationInfoState());
            }
          }}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </button>
      </div>
      {content}
      <Outlet />
      <Popup
        title="Reject application?"
        description="Application will be closed"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => {
          onRejectAplication();
        }}
        cancelButtonText="Cancel"
        okButtonText={rejectInProgress ? 'Loading...' : 'Reject'}
        isVisible={popupVisible}
      />
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() =>
          navigate(`${ROUTES.CUSTOMERS_NEW_APPLICATION_LIST}${search || ''}`)
        }
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
      />
      {successMessageVisible && (
        <div className={cn.successAppliedMessage}>
          <IconSelected />
          <div>Risk Score successfully updated to the Customer Application</div>
        </div>
      )}
      {}
      <a href={link} ref={linkRef} aria-label="error link" target="_self" />
      <div id="signature-popup" />
    </div>
  );
};

export default NewApplicationCorporateInfo;
